import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import products from '../data/products';
import '../styles.css';

const BASE_PRICES = {
  pro: 149,
  classic: 119,
  lite: 99,
  baby: 119  // --- IGNORE ---
};

/** Render a value that might be:
 *  - a plain string
 *  - an HTML string
 *  - an object like { html: "<p>...</p>" }
 *  - JSX (falls back to normal render)
 */
function renderHtml(val, className = '') {
  if (typeof val === 'string') {
    const looksLikeHtml = /<\/?[a-z][\s\S]*>/i.test(val);
    if (looksLikeHtml) {
      return <div className={className} dangerouslySetInnerHTML={{ __html: val }} />;
    }
    return <div className={className}>{val}</div>;
  }
  if (val && typeof val === 'object' && typeof val.html === 'string') {
    return <div className={className} dangerouslySetInnerHTML={{ __html: val.html }} />;
  }
  // Fallback (JSX or anything else)
  return <div className={className}>{val}</div>;
}

// Helper to turn the features object into bullet-point strings/HTML
function featureItems(features) {
  const f = features || {};
  const items = [];
  if (typeof f.aiAssistantTime === 'string' && f.aiAssistantTime.trim()) {
    // Keep original suffix while allowing upstream HTML
    items.push({ html: `${f.aiAssistantTime} AI assistant per month` });
  }
  if (f.slideshow) items.push('Slideshow');
  if (f.sharing) items.push('Sharing');
  // If you later add f.customBullets as array of strings/HTML/objects, concat here:
  // if (Array.isArray(f.customBullets)) items.push(...f.customBullets);
  return items;
}

export default function ProductList() {
  /** ------------------------------------------------------------------
   *  Testimonial data
   * ------------------------------------------------------------------ */
  const testimonials = [
    {
      title: 'New Memories, Daily',
      img: '/images/family-grandma.png',
      text:
        'The day we set **FotoMate** on Grandma’s nightstand, it felt like we’d handed her a tiny window into our world. Every afternoon, a fresh shot of Liam’s soccer goal or Emma’s science-fair ribbon appears automatically—no buttons, no cables, just a soft *ping* from FotoMate’s built-in voice assistant: “New photo from the kids!”<br /><br />When the slideshow pauses for the evening it slips into a graceful **grandfather-clock** face, and at dawn its gentle **alarm clock** chime welcomes her back to the day. At night she taps Nap Mode and the quiet **Sleep Machine** hushes the room. FotoMate never tracks her, never sells a single byte of data—just pure, private moments delivered with love.',
    },
    {
      title: 'Party Pics, Instant Magic',
      img: '/images/party-upload-1.png',
      text:
        'House party buzzing, lights low, and a **QR code** glows on the TV: “Beam your photos to FotoMate.” Friends snap, scan, and—boom—those candid dance-floor shots start gliding across the living-room screen in real time. A quick “Hey FotoMate, pause slideshow” freezes the perfect group selfie for cheers. <br /><br />Later, as the crowd winds down, FotoMate morphs into its classy **grandfather clock**, then fades into a soothing **Sleep Machine** backdrop that hums guests to their air-mattress dreams. Wake-up alarm at sunrise, no pesky ads, no data harvesting—just memories, music, and privacy, all in one elegant frame.',
    },
  ];

  // Pick one testimonial to display (stable during component lifecycle)
  const testimonial = useMemo(
    () => testimonials[Math.floor(Math.random() * testimonials.length)],
    []
  );

  return (
    <section id="products" className="product-list">
      <div><b>What is fotomate<sup>&reg;</sup>?</b>
        <p>
          
          fotomate is your personalized, customizable AI assistant—built to bring your memories to life.
        </p>
        <p>
          More than a smart companion, fotomate remembers details you’ve shared, helps organize your collection, and sparks meaningful conversations inspired by your photos and stories. It grows with you, weaving context into your family’s journey so memories feel alive, not archived.
        </p>
        <p>
          Each fotomate also arrives as a fully working slideshow, already preloaded with your photos. No setup, no hassle—just unbox and enjoy as forgotten and recent memories magically resurface, bringing families closer together every day.
        </p>
      </div>

      <div className="container">
        {/* ----------------------------------------------------------- */}
        {/* Product grid                                                */}
        {/* ----------------------------------------------------------- */}
        <h2 className="product-list__title">Our Editions</h2>
        <div className="product-list__grid">
          {products.map((p) => {
            const bullets = featureItems(p.features);
            const price = BASE_PRICES[p.id] != null ? `$${BASE_PRICES[p.id].toFixed(2)}` : '';
            return (
              <Link key={p.id} to={`/products/${p.id}`} className="card">
                <img
                  src={p.icon}
                  alt={`${typeof p.title === 'string' ? p.title.replace(/<[^>]+>/g, '') : 'Product'} icon`}
                  className="card__icon"
                />

                {/* Title (HTML allowed) */}
                {renderHtml(p.title, 'card__title')}

                {/* Byline (HTML allowed) */}
                {renderHtml(p.byline, '')}

                {/* ✅ Features as bullet points (each bullet can contain HTML or {html}) */}
                {bullets.length > 0 && (
                  <ul className="card__features">
                    {bullets.map((item, i) => (
                      <li key={i}>
                        {renderHtml(item)}
                      </li>
                    ))}
                  </ul>
                )}

                <p className="card__price">{price}</p>
              </Link>
            );
          })}
        </div>

        {/* ----------------------------------------------------------- */}
        {/* Supplemental Selling Point – Random Testimonial             */}
        {/* ----------------------------------------------------------- */}
        <div className="testimonial">
          <h3 className="product-list__subtitle">Why People Love FotoMate</h3>
          <h4 className="testimonial__title">{testimonial.title}</h4>

          {/* Floated image so copy wraps around it */}
          <img
            src={testimonial.img}
            alt={testimonial.title}
            className="testimonial__image testimonial__image--float"
          />

          {/* Rich HTML body */}
          <div
            className="testimonial__text"
            dangerouslySetInnerHTML={{ __html: testimonial.text }}
          />
        </div>
      </div>

      <style>{`
        .card {
          display: block;
          border: 1px solid var(--gray-border);
          border-radius: 0.5rem;
          padding: 1rem;
          text-decoration: none;
          color: inherit;
          transition: box-shadow 0.2s;
        }
        .card:hover {
          box-shadow: 0 4px 8px rgba(0,0,0,0.1);
        }
        .card__icon {
          margin-bottom: 0rem;
          width: 95%;
        }
        .card__title {
          font-size: 1.125rem;
          font-weight: 600;
        }
        .card__specs {
          font-size: 0.875rem;
          color: #4B5563;
        }

        /* ✅ Features list under each card */
        .card__features {
          margin: 0.25rem 0 0.5rem;
          padding-left: 1.1rem;
        }
        .card__features li {
          font-size: 0.9rem;
          color: #374151;
          line-height: 1.3;
        }

        /* Price under each card */
        .card__price {
          font-size: 1rem;
          font-weight: 600;
          color: var(--blue-primary);
          margin-top: 0.25rem;
        }

        /* ============================= */
        /* Testimonial wrap-around styles */
        /* ============================= */
        .testimonial {
          max-width: 90%;
          margin: 2rem auto 3rem;
          line-height: 1.6;
          position: relative;
          /* clearfix for floated image */
        }
        .testimonial::after {
          content: "";
          display: table;
          clear: both;
        }
        .testimonial__title {
          margin: 0.25rem 0 0.75rem;
          font-size: 1.125rem;
          font-weight: 600;
        }
        .testimonial__image {
          width: 360px;
          height: auto;
          display: block;
        }
        .testimonial__image--float {
          float: left;           /* ✨ key for wrap-around */
          margin: 0.25rem 1.25rem 0.75rem 0; /* space so text doesn't butt up against img */
          max-width: 40%;
          min-width: 220px;
        }
        .testimonial__text p { margin: 0 0 1rem; }

        /* Responsive: stack on narrow viewports */
        @media (max-width: 768px) {
          .testimonial {
            max-width: 100%;
            padding: 0 1rem;
          }
          .testimonial__image--float {
            float: none;
            max-width: 100%;
            min-width: 0;
            margin: 0 0 1rem 0;
          }
        }
      `}</style>
    </section>
  );
}
